import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize, Logger } from '@saviynt/common';
import { Checkbox } from '@saviynt/design-system';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import {
  resetDynamicSortDetails,
  setIsBulkActionsCompleted,
  setPersonalizationStatus,
  setSelectedRowFilters,
  setSidePanelVisible,
  triggerFilterChange,
} from '../../../redux/certdetails/action';
import {
  selectAllBulkAction,
  setAlteastOneIsSelected,
  setBulkActionAlert,
  setBulkActionBySelectAll,
  setBulkActionFooterData,
  setBulkFooterShow,
  setCheckForBulkAction,
  setIndeterminateForBulkAction,
} from '../../../redux/certdetails/bulkaction/action';
import { GROUP_BY_APP_VALUE } from '../../../utils/constant';
import { getMessageKey } from '../../../utils/utils';
import { CERT_ENTITLEMENT_VALUEKEY } from '../DynamicSort/constants';

import { assignCountToRowFilterTree } from './utils';

function RowsFilter({
  filters,
  appliedRowFilters,
  setAppliedRowFilters,
  level,
  onClose,
}) {
  const dispatch = useDispatch();
  const intl = useIntl();

  const [isEntireFilterDisabled, setIsEntireFilterDisabled] = useState(false);
  // Set initial state, only needed for the Menu open transition
  const updatedFiltersWithCount = assignCountToRowFilterTree(
    filters,
    appliedRowFilters
  );
  const [nestedFilters, setNestedFilters] = useState(updatedFiltersWithCount);

  // TODO: Use redux rowFilters instead of calling assignCountToRowFilterTree twice every mount
  const {
    global: { certKey },
    bulkAction: {
      isChecked: isBulkChecked,
      isIndeterminate: isBulkIndeterminate,
    },
    viewControls: { groupByOptions },
  } = useSelector((state) => state.certifications?.details);

  const { messages } = useSelector((state) => state?.user);

  const selectedGroupByValue = groupByOptions?.find(
    (option) => option.isChecked
  )?.value;
  const isUsingGroupBy = selectedGroupByValue === GROUP_BY_APP_VALUE;

  const TYPE_TITLES = {
    Role:
      messages['Role_LABEL.ATTESTATIONS'] ||
      localize(intl, getMessageKey('Role_LABEL.ATTESTATIONS')),
    Account:
      messages['Account_LABEL.ATTESTATIONS'] ||
      localize(intl, getMessageKey('Account_LABEL.ATTESTATIONS')),
    User:
      messages['User_LABEL.ATTESTATIONS'] ||
      localize(intl, getMessageKey('User_LABEL.ATTESTATIONS')),
    Entitlement:
      messages['Ent_LABEL.ATTESTATIONS'] ||
      localize(intl, getMessageKey('Ent_LABEL.ATTESTATIONS')),
    'Child Entitlement':
      messages['Ent2_LABEL.ATTESTATIONS'] ||
      localize(intl, getMessageKey('Ent2_LABEL.ATTESTATIONS')),
  };

  // Sync applied filters isChecked on mount
  useEffect(() => {
    setNestedFilters(updatedFiltersWithCount);
  }, []);

  // Handle ESC key to close menu (only at root level)
  useEffect(() => {
    if (level !== 0 || !onClose) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [level, onClose]);

  const handleToggle = (filterValue) => {
    dispatch(resetDynamicSortDetails());

    if (appliedRowFilters.includes(filterValue)) {
      if (appliedRowFilters.length === 1) {
        Logger.warn('Atleast one filter is required');

        return;
      }

      setIsEntireFilterDisabled(true);
      dispatch(setSidePanelVisible(false));

      const updatedRowFilters = appliedRowFilters.filter(
        (item) => item !== filterValue
      );

      dispatch(setSelectedRowFilters(updatedRowFilters));
      dispatch(triggerFilterChange());

      setAppliedRowFilters(updatedRowFilters);
      setIsEntireFilterDisabled(false);
    } else {
      const newAppliedFilters = [...appliedRowFilters, filterValue];

      setIsEntireFilterDisabled(true);
      dispatch(setSidePanelVisible(false));

      dispatch(setSelectedRowFilters(newAppliedFilters));
      dispatch(triggerFilterChange());
      setAppliedRowFilters(newAppliedFilters);
      setIsEntireFilterDisabled(false);
    }

    setNestedFilters((prev) =>
      prev.map((filter) => {
        if (filter.value === filterValue) {
          return {
            ...filter,
            isChecked: !filter.isChecked,
          };
        }

        return filter;
      })
    );

    dispatch(setPersonalizationStatus('UPDATE_PERSONALIZATION'));
    dispatch(setIsBulkActionsCompleted(false));

    // CR-22536: Reset bulk state synchronously before selectAllBulkAction so
    // triggerFilterChange (fired immediately after) sees a clean state.
    if (isBulkChecked || isBulkIndeterminate) {
      dispatch(setBulkActionBySelectAll(true));
      dispatch(setAlteastOneIsSelected(false));
      dispatch(setBulkFooterShow(false));
      dispatch(setCheckForBulkAction(false));
      dispatch(setIndeterminateForBulkAction(false));

      dispatch(selectAllBulkAction(certKey, true)).catch((error) => {
        dispatch(setBulkActionAlert(true, error.message));
      });
    }

    dispatch(setBulkFooterShow(false));
    dispatch(setBulkActionFooterData(null));
  };

  useEffect(() => {
    if (appliedRowFilters?.length === 1) {
      setNestedFilters((prev) =>
        prev.map((filter) => ({
          ...filter,
          isLast: filter.value === appliedRowFilters[0],
        }))
      );
    }

    if (appliedRowFilters?.length > 1) {
      setNestedFilters((prev) =>
        prev.map((filter) => ({
          ...filter,
          isLast: false,
        }))
      );
    }
  }, [appliedRowFilters]);

  // disable selection for rows when group by applied
  const disableSelectionRowForGroup =
    appliedRowFilters.length === 2 &&
    isUsingGroupBy &&
    appliedRowFilters.includes(CERT_ENTITLEMENT_VALUEKEY.USER);

  const rowFiltersLabel =
    messages['RowFilters_LABEL.ATTESTATIONS'] ||
    localize(intl, getMessageKey('RowFilters_LABEL.ATTESTATIONS'));

  const isRootLevel = level === 0;

  return (
    <ul
      className='RowsFilter-filterList'
      role={isRootLevel ? 'tree' : 'group'}
      aria-label={isRootLevel ? rowFiltersLabel : undefined}>
      {nestedFilters?.map((filter) => {
        const hasChildren = filter?.options?.length > 0;

        return (
          <li
            key={filter?.value}
            role='treeitem'
            aria-selected={filter?.isChecked}
            aria-expanded={hasChildren ? true : undefined}>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className='RowsFilter-filterListItem'>
              <div
                className={classNames(
                  'RowsFilter-filterListItemWrapper',
                  filter?.label === 'User' &&
                    isUsingGroupBy &&
                    'RowsFilter-item--isUsingGroupBy'
                )}
                style={{
                  transform: `translateX(-${level * 1.125}rem)`,
                  width: `calc(100% + ${level * 1.125 + 0.875}rem)`,
                }}
              />
              {!filter?.isParent && (
                <div
                  className='RowsFilter-filterListItemConnectors--top'
                  style={{ height: `${(filter.count + 1) * 2.375}rem` }}
                />
              )}
              <div className='RowsFilter-filterListItemCheckboxParent'>
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <div
                  className='RowsFilter-filterListItemCheckboxChild'
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isEntireFilterDisabled) {
                      handleToggle(filter?.value);
                    }
                  }}>
                  <Checkbox
                    id='rows-filter-checkbox'
                    name={filter?.value}
                    isChecked={
                      (isUsingGroupBy && filter?.label === 'User') ||
                      filter?.isChecked ||
                      filter?.isDisabled
                    }
                    onChange={() => {
                      if (!isEntireFilterDisabled) handleToggle(filter?.value);
                    }}
                    isDisabled={
                      (filter?.label === 'User' && isUsingGroupBy) ||
                      filter?.isDisabled ||
                      filter?.isLast ||
                      isEntireFilterDisabled ||
                      (disableSelectionRowForGroup && filter?.isChecked)
                    }
                    dataTestId='checkbox-no-hovereffect'
                  />
                </div>
              </div>
              <p className='RowsFilter-filterListItemTextBox'>
                {TYPE_TITLES[filter?.label]}
              </p>
            </label>
            {hasChildren && (
              <RowsFilter
                filters={filter?.options}
                appliedRowFilters={appliedRowFilters}
                setAppliedRowFilters={setAppliedRowFilters}
                isEntireFilterDisabled={isEntireFilterDisabled}
                setIsEntireFilterDisabled={setIsEntireFilterDisabled}
                level={level + 1}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

RowsFilter.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      isChecked: PropTypes.bool.isRequired,
      isParent: PropTypes.bool,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.string,
          isChecked: PropTypes.bool.isRequired,
        })
      ),
    })
  ).isRequired,
  appliedRowFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
  setAppliedRowFilters: PropTypes.func.isRequired,
  level: PropTypes.number,
  onClose: PropTypes.func,
};

RowsFilter.defaultProps = { level: 0, onClose: null };

export default RowsFilter;
